<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: { type: Object, default: null },
  timezones:  { type: Array,  default: () => [] },
})
const emit = defineEmits(['update:modelValue'])

const isOpen     = ref(false)
const searchTerm = ref('')

const filtered = computed(() => {
  if (!searchTerm.value) return props.timezones
  const q = searchTerm.value.toLowerCase()
  const exact = props.timezones.filter(z => z.name.toLowerCase() === q)
  return exact.length ? exact : props.timezones.filter(z =>
    z.name.toLowerCase().includes(q) || z.offset.includes(q)
  )
})

const label = computed(() =>
  props.modelValue
    ? `(GMT${props.modelValue.offset}) ${props.modelValue.name}`
    : 'Select timezone'
)

function select(tz) {
  emit('update:modelValue', tz)
  isOpen.value   = false
  searchTerm.value = ''
}

function toggle() {
  isOpen.value = !isOpen.value
  if (!isOpen.value) searchTerm.value = ''
}

function handleOutsideClick(event) {
  if (!(event.target instanceof Element)) return
  if (!event.target.closest('.timezone-selector-root')) {
    isOpen.value = false
    searchTerm.value = ''
  }
}

onMounted(() => document.addEventListener('click', handleOutsideClick))
onUnmounted(() => document.removeEventListener('click', handleOutsideClick))
</script>

<template>
  <div class="relative timezone-selector-root">
    <!-- Trigger (matches profile page's subtle row style) -->
    <button @click="toggle"
            type="button"
            :aria-expanded="isOpen"
            aria-haspopup="listbox"
            class="w-full rounded-[5px] bg-gray-100 hover:bg-gray-200/80 dark:bg-gray-700
                   dark:hover:bg-gray-600 px-4 py-3 flex items-center gap-2.5
                   text-sm font-medium text-gray-700 dark:text-gray-200 transition-colors
                   cursor-pointer text-left">
      <svg class="w-3.5 h-3.5 fill-blue-500 flex-shrink-0" viewBox="0 0 512 512">
        <path d="M57.7 193l9.4 16.4c8.3 14.5 21.9 25.2 38 29.8L163 255.7c17.2 4.9 29 20.6 29 38.5v39.9c0 11 6.2 21 16 25.9s16 14.9 16 25.9v39c0 15.6 14.9 26.9 29.9 22.6c16.1-4.6 28.6-17.5 32.7-33.8l2.8-11.2c4.2-16.9 15.2-31.4 30.3-40l8.1-4.6c15-8.5 24.2-24.5 24.2-41.7v-8.3c0-12.7-5.1-24.9-14.1-33.9l-3.9-3.9c-9-9-21.2-14.1-33.9-14.1H257c-11.1 0-22.1-2.9-31.8-8.4l-34.5-19.7c-4.3-2.5-7.6-6.5-9.2-11.2c-3.2-9.6 1.1-20 10.2-24.5l5.9-3c6.6-3.3 14.3-3.9 21.3-1.5l23.2 7.7c8.2 2.7 17.2-.4 21.9-7.5c4.7-7 4.2-16.3-1.2-22.8l-13.6-16.3c-10-12-9.9-29.5 .3-41.3l15.7-18.3c8.8-10.3 10.2-25 3.5-36.7l-2.4-4.2c-3.5-.2-6.9-.3-10.4-.3C163.1 48 84.4 108.9 57.7 193zM464 256c0-36.8-9.6-71.4-26.4-101.5L412 164.8c-15.7 6.3-23.8 23.8-18.5 39.8l16.9 50.7c3.5 10.4 12 18.3 22.6 20.9l29.1 7.3c1.2-9 1.8-18.2 1.8-27.5zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256z"/>
      </svg>
      <span class="grow truncate">{{ label }}</span>
      <svg class="w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform"
           :class="isOpen ? 'rotate-180' : ''"
           fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>

    <!-- Dropdown panel -->
    <div v-if="isOpen"
         class="absolute z-30 bottom-full mb-1 w-full max-h-64 flex flex-col
                rounded-[5px] bg-white dark:bg-[#1C2128]
                shadow-lg ring-1 ring-gray-200 dark:ring-gray-700
                overflow-hidden">
      <!-- Search -->
      <div class="flex items-center gap-2 border-b border-gray-100 dark:border-gray-700 px-3">
        <svg class="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none"
             stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input v-model="searchTerm"
               autocomplete="off" autocorrect="off" spellcheck="false"
               placeholder="Search timezone"
               class="w-full py-2.5 text-sm bg-transparent border-none outline-none
                      text-gray-700 dark:text-gray-200 placeholder-gray-400
                      dark:placeholder-gray-500 focus:ring-0" />
      </div>
      <!-- Results -->
      <div class="overflow-y-auto scrollbar-style">
        <button v-for="tz in filtered"
                :key="tz.name"
                @click="select(tz)"
                type="button"
                class="w-full px-4 py-2.5 text-left flex items-center gap-2
                       hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors">
          <span class="rounded-full bg-gray-100 dark:bg-gray-700 px-1.5 py-px
                       text-[11px] font-medium text-gray-500 dark:text-gray-400
                       tabular-nums flex-shrink-0">
            {{ tz.offset }}
          </span>
          <span class="grow text-sm text-gray-700 dark:text-gray-200 truncate">{{ tz.name }}</span>
          <time class="text-xs text-gray-400 dark:text-gray-500 tabular-nums flex-shrink-0">
            {{ tz.time }}
          </time>
        </button>
      </div>
    </div>
  </div>
</template>
